import { mergeAggregationGroups } from "@/components/controls/DataTable/groupSummaries";
import { useMemo } from "react";
import useAPI from "@/services/api/hook";
import { APIFieldsResponse, APIFieldItem } from "@/pages/settings/types";
import { FieldTypeLevel } from "@/pages/settings/hooks";
import {
  ComputedFields,
  ExtendedUseQueryResult,
  Filters,
  GenericAggregations,
  SearchQuery
} from "@/services/api/types";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { ServiceData } from "./types";

export const SERVICES_PAGE_SIZE = 50;

export const useServiceFields = ({
  enabled = true,
  fieldTypeLevel
}: {
  enabled?: boolean;
  fieldTypeLevel?: FieldTypeLevel;
} = {}): ExtendedUseQueryResult<APIFieldsResponse> => {
  const result = useAPI(
    "settings/revenueItems/fields",
    {
      queryParams: {
        ...(fieldTypeLevel && { field_type_level: fieldTypeLevel })
      }
    },
    { enabled }
  );

  const filteredData = useMemo(() => {
    return result.data
      ? {
          ...result.data,
          fields:
            result.data.fields?.filter(
              (field: APIFieldItem) => !field.isDeleted
            ) || []
        }
      : result.data;
  }, [result.data]);

  return useMemo(
    () => ({ ...result, data: filteredData }),
    [result, filteredData]
  );
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetFilteredServices
} = createUseFilteredEntitiesInfinite<ServiceData>(
  "contracts/serviceItems",
  SERVICES_PAGE_SIZE,
  {
    responseKey: ["revenue_items", "revenueItemsEnriched"],
    extraQueryParams: { include_additionals: "false" },
    defaultAggregations: { groupBy: "all" }
  }
);

export interface ServicesAggregations {
  totalCount: number;
  totalValue: number;
}

interface ServicesAggregationField {
  name: string;
  aggregations: { sum?: number | null };
}

const parseServicesAggregations = (raw: Record<string, unknown>): ServicesAggregations => {
  // The response is grouped whenever the table is, so the whole-set figures
  // come from every group merged back together, not from the first one.
  const group = mergeAggregationGroups(
    raw as unknown as GenericAggregations<string>
  ) as
    | { total_count?: number; fields?: ServicesAggregationField[] }
    | undefined;
  const fields = group?.fields;
  return {
    totalCount: group?.total_count ?? 0,
    totalValue: fields?.find((f) => f.name === "total")?.aggregations?.sum ?? 0
  };
};

const useNoopStore = () => ({ addEntity: () => {} });

export const useFilteredServices = (
  sortField?: string,
  sortAsc?: boolean,
  filters: Filters = {},
  searchTerm = "",
  enabled = true,
  groupedBy?: string
) => {
  const search: SearchQuery | undefined = useMemo(
    () => (searchTerm ? { term: searchTerm } : undefined),
    [searchTerm]
  );

  const result = useFilteredEntitiesInfinite(useNoopStore, {
    filters,
    search,
    sortField,
    sortAsc,
    enabled,
    groupedBy: groupedBy as never,
    computed_fields: ComputedFields.MINIMAL
  });

  const aggregations = useMemo(
    () => parseServicesAggregations(result.aggregations as Record<string, unknown>),
    [result.aggregations]
  );

  /** Grouped as requested — one entry per distinct value of `groupedBy`. */
  const rawAggregations = result.aggregations as unknown as
    | GenericAggregations<string>
    | undefined;

  return {
    services: result.entities,
    total: aggregations.totalCount || result.total,
    aggregations,
    rawAggregations,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    isLoading: result.isLoading,
    isAggregationsLoading: result.isAggregationsLoading,
    isAggregationsError: result.isAggregationsError,
    isError: result.isError,
    refetch: result.refetch
  };
};
